import classNames from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Body1, Heading6 } from '@ui/Typography'

import { RegulatorProductOption } from '@model/RegulatorOptionDto'
import { TickmillProductType } from '@model/TickmillProductType'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'

import { ProductField } from '../../../Components/ProductField'

import sharedStyles from '../PersonalDetailsPage.module.scss'

interface ProductSelectionProps {
  products: RegulatorProductOption[]
  productIds: TickmillProductType[]
  onProductChange: (productIds: TickmillProductType[]) => void
  title?: string
  error?: string
  className?: string
  isLoading?: boolean
}

export const ProductSelection: React.FC<ProductSelectionProps> = ({
  products,
  productIds = [],
  onProductChange,
  title,
  error,
  className,
  isLoading = false,
}) => {
  const { t } = useTranslation()

  const handleChangeProductIds = (checked: boolean, productId: TickmillProductType) => {
    const newProductIds = checked
      ? [...productIds, productId]
      : productIds.filter((p) => p !== productId)

    onProductChange(newProductIds)
  }

  const hasProductIdsError = !!error && productIds.length === 0

  if (products.length === 0 && !isLoading) {
    return null
  }

  const displayTitle = title ?? t('Sign up.Select Product')

  return (
    <Loading isLoading={isLoading} showLoadingIcon>
      <Box flex direction='column' gap='x16' className={className}>
        <Heading6 medium>{displayTitle}</Heading6>

        <div
          className={classNames('control', className, {
            'has-error': hasProductIdsError,
          })}
        >
          <div className={sharedStyles.productContainer}>
            {products.map((product, index) => (
              <ProductField
                key={index}
                tickmillProduct={product}
                onChangeProductIds={handleChangeProductIds}
                minDeposit={product.depositLimitText}
                productIds={productIds}
              >
                <Body1>{product.name}</Body1>
              </ProductField>
            ))}
          </div>
          {hasProductIdsError && <Body1 className='has-error'>{error}</Body1>}
        </div>
      </Box>
    </Loading>
  )
}
